// Shared geocoding helper: address → {lat, lng} via OSM Nominatim with LocalStorage cache + throttle.

const GEO_CACHE_KEY = "if_geo_cache_v1";

function readCache() {
  try { return JSON.parse(localStorage.getItem(GEO_CACHE_KEY) || "{}"); } catch { return {}; }
}
function writeCache(c) {
  try { localStorage.setItem(GEO_CACHE_KEY, JSON.stringify(c)); } catch { /* ignore */ }
}

export async function geocodeAddress(address) {
  if (!address || !address.trim()) return null;
  const cache = readCache();
  const key = address.trim().toLowerCase();
  if (cache[key] !== undefined) return cache[key]; // null also cached
  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=de&q=${encodeURIComponent(address)}`;
    const r = await fetch(url, { headers: { "Accept-Language": "de" } });
    const data = await r.json();
    const result = data?.[0] ? { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) } : null;
    cache[key] = result;
    writeCache(cache);
    return result;
  } catch {
    return null;
  }
}

// Process unique addresses sequentially with a delay (Nominatim policy: ≤1/sec)
export async function geocodeBatch(addresses, onItem, delayMs = 1100) {
  const seen = new Set();
  for (const addr of addresses) {
    if (!addr || seen.has(addr)) continue;
    seen.add(addr);
    const cached = readCache()[addr.trim().toLowerCase()];
    if (cached !== undefined) {
      onItem(addr, cached);
      continue;
    }
    const res = await geocodeAddress(addr);
    onItem(addr, res);
    await new Promise((r) => setTimeout(r, delayMs));
  }
}

// Haversine distance in km
export function haversineKm(a, b) {
  if (!a || !b) return null;
  const toRad = (x) => (x * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

// Photon (komoot.io) autocomplete — free, OSM-based, designed for typeahead.
// Returns up to `limit` suggestions: [{label, lat, lng, raw}, ...]
async function fetchPhoton(query, limit, signal) {
  try {
    const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=${limit}&lang=de&osm_tag=!boundary`;
    const r = await fetch(url, { signal });
    const data = await r.json();
    const feats = data?.features || [];
    return feats.map((f) => {
      const p = f.properties || {};
      const parts = [];
      if (p.name && !p.street) parts.push(p.name);   // e.g. POI name when no street
      const street = p.street ? `${p.street}${p.housenumber ? " " + p.housenumber : ""}` : "";
      if (street) parts.push(street);
      const cityLine = [p.postcode, p.city || p.town || p.village || p.county]
        .filter(Boolean).join(" ");
      if (cityLine) parts.push(cityLine);
      if (p.country) parts.push(p.country);
      const [lng, lat] = f.geometry?.coordinates || [];
      return {
        label: parts.join(", "),
        lat,
        lng,
        hasHousenumber: !!p.housenumber,
        source: "photon",
      };
    }).filter((s) => s.lat != null && s.lng != null && s.label);
  } catch {
    return [];
  }
}

// Nominatim — slower (1 req/sec), but richer house-number coverage.
async function fetchNominatim(query, limit, signal) {
  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=${limit}&countrycodes=de&q=${encodeURIComponent(query)}`;
    const r = await fetch(url, { signal, headers: { "Accept-Language": "de" } });
    const data = await r.json();
    if (!Array.isArray(data)) return [];
    return data.map((item) => {
      const a = item.address || {};
      const street = a.road ? `${a.road}${a.house_number ? " " + a.house_number : ""}` : "";
      const cityLine = [a.postcode, a.city || a.town || a.village || a.suburb || a.county]
        .filter(Boolean).join(" ");
      const parts = [street, cityLine, a.country].filter(Boolean);
      const label = parts.join(", ") || item.display_name;
      return {
        label,
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lon),
        hasHousenumber: !!a.house_number,
        source: "nominatim",
      };
    }).filter((s) => s.lat != null && s.lng != null && s.label);
  } catch {
    return [];
  }
}

/**
 * Autocomplete with smart fallback:
 *  - Always queries Photon (fast, free, ~50–150 ms).
 *  - If query contains a digit (likely a house number) AND Photon didn't return
 *    any result *with* a housenumber, ALSO queries Nominatim and merges results
 *    (Nominatim hits first, then Photon, deduped).
 */
export async function photonSuggest(query, { limit = 5, signal } = {}) {
  if (!query || query.trim().length < 3) return [];
  const photon = await fetchPhoton(query, limit, signal);

  const hasDigit = /\d/.test(query);
  const photonHasHouseNumber = photon.some((s) => s.hasHousenumber);

  if (hasDigit && !photonHasHouseNumber) {
    const nominatim = await fetchNominatim(query, limit, signal);
    if (nominatim.length) {
      // Merge: Nominatim first (housenumber precision), then unique Photon
      const seen = new Set();
      const out = [];
      for (const s of [...nominatim, ...photon]) {
        const key = `${s.lat.toFixed(4)},${s.lng.toFixed(4)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(s);
        if (out.length >= limit) break;
      }
      return out;
    }
  }
  return photon;
}
